import classNames from 'classnames';
import React from 'react';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { Ribbon, BestsellerRibbon, HotAndNewRibbon, NewRibbon, HighestRatedRibbon } from '@udemy/react-messaging-components';
import { Tag } from '@udemy/react-product-card-components';
import { COURSE_BADGE_BESTSELLER, COURSE_BADGE_CODING_EXERCISES, COURSE_BADGE_FREE, COURSE_BADGE_HIGHEST_RATED, COURSE_BADGE_HOT_AND_NEW, COURSE_BADGE_INCLUDED_IN_PLANS, COURSE_BADGE_NEW, COURSE_BADGE_PREMIUM, COURSE_BADGE_TOP_PICK, COURSE_BADGE_UPDATED_RECENTLY, COURSE_BADGE_ROLE_PLAY, COURSE_BADGE_LABS, COURSE_BADGE_PRACTICE_TESTS, COURSE_BADGE_HANDS_ON_LEARNING, COURSE_BADGE_QUIZZES, COURSE_BADGE_WORKSPACES, COURSE_BADGE_GROW_WITH_GOOGLE } from './constants';
import styles from "./course-badges.module.css";

/** The possible types that a Course Badge can be  */

// TODO: Delete this after the experiment is over https://udemyjira.atlassian.net/browse/CVR-151
export const COURSE_IN_SUBS_PLAN_BADGE_FAMILIES = [COURSE_BADGE_TOP_PICK, COURSE_BADGE_PREMIUM, COURSE_BADGE_INCLUDED_IN_PLANS];
export const COURSE_BADGE_FAMILIES = [COURSE_BADGE_BESTSELLER, COURSE_BADGE_HIGHEST_RATED, COURSE_BADGE_NEW, COURSE_BADGE_HOT_AND_NEW, COURSE_BADGE_FREE, COURSE_BADGE_CODING_EXERCISES, COURSE_BADGE_UPDATED_RECENTLY, COURSE_BADGE_ROLE_PLAY, COURSE_BADGE_LABS, COURSE_BADGE_PRACTICE_TESTS, COURSE_BADGE_HANDS_ON_LEARNING, COURSE_BADGE_QUIZZES, COURSE_BADGE_WORKSPACES, COURSE_BADGE_GROW_WITH_GOOGLE, ...COURSE_IN_SUBS_PLAN_BADGE_FAMILIES];
export const TopPickBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    className: classNames(styles.subscription_badge, className)
  }, /*#__PURE__*/React.createElement(Icon, {
    color: "inherit",
    size: "xsmall",
    glyph: "verified",
    label: false
  }), gettext('Top pick'));
};
export const IncludedInPlansBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    className: classNames(styles.subscription_badge, className)
  }, /*#__PURE__*/React.createElement(Icon, {
    color: "inherit",
    size: "xsmall",
    glyph: "video-playlist",
    label: false
  }), gettext('Included in plans'));
};
export const PremiumBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    className: classNames(styles.subscription_badge, className)
  }, /*#__PURE__*/React.createElement(Icon, {
    color: "inherit",
    size: "xsmall",
    glyph: "verified",
    label: false
  }), gettext('Premium'));
};

/**
 * ### Free Badge
 */
export const FreeBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "red-150",
    className: className
  }, gettext('Free tutorial'));
};

/**
 * ### Updated Recently Badge
 */
export const UpdatedRecentlyBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "green-150",
    className: className
  }, gettext('Updated Recently'));
};

/**
 * ### Coding Exercises Badge
 */
export const CodingExercisesBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "purple-150",
    className: className
  }, gettext('Coding Exercises'));
};

/**
 * ### Role Play Badge
 */
export const RolePlayBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return (
    /*#__PURE__*/
    // TODO Update color after final designs are shared
    React.createElement(Ribbon, {
      color: "blue-200",
      className: className
    }, gettext('Role Play'))
  );
};

/**
 * ### Labs Badge
 */
export const LabsBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "purple-150",
    className: className
  }, gettext('Labs'));
};

/**
 * ### Practice Tests Badge
 */
export const PracticeTestsBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "purple-150",
    className: className
  }, gettext('Practice tests'));
};

/**
 * ### Hands-on Learning Badge
 */
export const HandsOnLearningBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "purple-150",
    className: className
  }, gettext('Hands-on learning'));
};

/**
 * ### Quizzes Badge
 */
export const QuizzesBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "purple-150",
    className: className
  }, gettext('Quizzes'));
};

/**
 * ### Workspaces Badge
 */
export const WorkspacesBadge = ({
  className
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    color: "purple-150",
    className: className
  }, gettext('Workspaces'));
};

/**
 * ### Grow with Google Badge
 * Displays just the Google icon with padding and border
 */
export const GrowWithGoogleBadge = () => {
  return /*#__PURE__*/React.createElement(Tag, {
    key: "google-logo-tag"
  }, /*#__PURE__*/React.createElement(Icon, {
    size: "xxxsmall",
    glyph: "google",
    label: ""
  }));
};

/** An array of badges used for a Course Card */

/**
 * A utility to return the correct Badge component but looking up the bageType and returning the corresponding component.
 *
 * @param course a CourseApiBadges object
 * @returns the correct Badge course component unless not specified, then null otherwise
 */
export const getCourseBadge = course => {
  const badgeType = getCourseBadgeType(course);
  return badgeType ? getCourseBadgeFromType(badgeType) : null;
};

/**
 * A utility to get the course badge based on its type.
 *
 * @param course a CourseApiBadges object
 * @returns the badge type string or undefined if not found
 */
export const getCourseBadgeType = course => {
  // Include course.badges in left side of the conditional operator as guard for TypeScript
  return courseHasBadge(course) && course.badges ? course.badges[0].badge_family : undefined;
};

/**
 * Utility to help determine if there are badges for a course
 *
 * @param course a CourseApiBadges object
 * @returns boolean true if course has a badge, false otherwise
 */
export const courseHasBadge = course => {
  return !!(course.badges && course.badges.length > 0 && course.badges[0]);
};

/**
 * Return a badge from an array of strings corresponding to `badge_family`.
 *
 * @param badges - array of values from course badges `badge_family`
 * @returns the Badge component matching the first badge family in the array; null if the array is empty
 *
 * @remarks
 *
 * Corresponds to the interface for the `badges` function in `@udemy/react-card-components#badgesPropsFrom`
 */
export const getCourseBadgeFromBadgesFamily = badges => badges.length > 0 ? getCourseBadgeFromType(badges[0]) : null;

/**
 * A utility to return the correct Badge component to return based on the constant type.
 *
 * @param type string from the constants
 * @returns The corresponding Course Badge component
 */
export function getCourseBadgeFromType(type) {
  switch (type) {
    case COURSE_BADGE_BESTSELLER:
      return BestsellerRibbon;
    case COURSE_BADGE_FREE:
      return FreeBadge;
    case COURSE_BADGE_HOT_AND_NEW:
      return HotAndNewRibbon;
    case COURSE_BADGE_HIGHEST_RATED:
      return HighestRatedRibbon;
    case COURSE_BADGE_NEW:
      return NewRibbon;
    case COURSE_BADGE_UPDATED_RECENTLY:
      return UpdatedRecentlyBadge;
    case COURSE_BADGE_INCLUDED_IN_PLANS:
      return IncludedInPlansBadge;
    case COURSE_BADGE_TOP_PICK:
      return TopPickBadge;
    case COURSE_BADGE_PREMIUM:
      return PremiumBadge;
    case COURSE_BADGE_CODING_EXERCISES:
      return CodingExercisesBadge;
    case COURSE_BADGE_ROLE_PLAY:
      return RolePlayBadge;
    case COURSE_BADGE_LABS:
      return LabsBadge;
    case COURSE_BADGE_PRACTICE_TESTS:
      return PracticeTestsBadge;
    case COURSE_BADGE_HANDS_ON_LEARNING:
      return HandsOnLearningBadge;
    case COURSE_BADGE_QUIZZES:
      return QuizzesBadge;
    case COURSE_BADGE_WORKSPACES:
      return WorkspacesBadge;
    case COURSE_BADGE_GROW_WITH_GOOGLE:
      return GrowWithGoogleBadge;
    default:
      return undefined;
  }
}
export const getBadgeConditionForCourse = type => {
  switch (type) {
    case COURSE_BADGE_INCLUDED_IN_PLANS:
    case COURSE_BADGE_TOP_PICK:
      return course => course.is_in_personal_plan_collection;
    case COURSE_BADGE_PREMIUM:
      return course => course.is_in_personal_plan_collection && course.is_paid;
    // only show premium badge for paid courses
    case COURSE_BADGE_GROW_WITH_GOOGLE:
      return course => {
        var _course$contentCollec;
        return !!((_course$contentCollec = course.contentCollectionInfo) !== null && _course$contentCollec !== void 0 && _course$contentCollec.isInGooglePartnerCollection);
      };
    default:
      return course => course.badges && course.badges.map(badge => badge.badge_family).includes(type);
  }
};
export class BrowseCourseBadgeHandler {
  constructor(course, options) {
    this.course = void 0;
    this.options = void 0;
    this.defaultOptions = {
      numberOfBadgesToShow: 1,
      /**
       * Filter out the subs plan badges by default.
       * Currently the subs plan badges are: 'top_pick', 'premium', 'included_in_plans' and they are tied to
       * an experiment. We don't want to accidentally expose them to users who are not part of the experiment.
       *
       * TODO: Remove the filter when the experiment is over https://udemyjira.atlassian.net/browse/CVR-151
       * @param badge
       * @remarks Be aware of that, once you define a filter, it will override the default filter
       */
      filter: badge => !COURSE_IN_SUBS_PLAN_BADGE_FAMILIES.includes(badge),
      priority: {},
      badgeProps: {
        className: ''
      }
    };
    /**
     * Update the options for the badge handler.
     * @param newOptions - The new options to update the badge handler with.
     * @remarks This will merge the new options with the existing options.
     */
    this.updateOptions = newOptions => {
      this.options = {
        ...this.options,
        ...newOptions
      };
    };
    /**
     * Compare function to sort badges by priority defined in options.
     *
     * @param a - badge family
     * @param b - badge family
     */
    this.badgeCompareFuncByPriority = (a, b) => {
      const priority = this.options.priority;
      if (priority && Object.keys(priority).length > 0) {
        var _priority$a, _priority$b;
        const orderA = (_priority$a = priority[a]) !== null && _priority$a !== void 0 ? _priority$a : Number.MAX_SAFE_INTEGER;
        const orderB = (_priority$b = priority[b]) !== null && _priority$b !== void 0 ? _priority$b : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      }
      // Keep original order
      return 0;
    };
    /**
     * Get the available badge families for the course based on the course's properties.
     * @returns an array of badge families that are available for the course
     *
     * @remarks filter is not applied here. This will return all the available badge families for the given course
     */
    this.getAvailableBadgeFamiliesWithoutFilter = () => COURSE_BADGE_FAMILIES.filter(family => getBadgeConditionForCourse(family)(this.course));
    /**
     * Get the badge component for the course based on the badge family, if course is available for the badge
     * @param type
     *
     * @remarks This is not using options that provide by the user. This will return the badge component for the given badge family
     */
    this.getBadgeComponent = type => {
      // First check jf badge is available
      const isBadgeAvailable = getBadgeConditionForCourse(type)(this.course);
      if (isBadgeAvailable) {
        return getCourseBadgeFromType(type);
      }
      return undefined;
    };
    /**
     * Get the badge families for the course based on the options provided.
     * @returns an array of badge families
     */
    this.getBadgeFamilies = () => {
      return this.getAvailableBadgeFamiliesWithoutFilter() // Get Available Badges
      .filter(badge => {
        var _this$options$filter, _this$options;
        return (_this$options$filter = (_this$options = this.options).filter) === null || _this$options$filter === void 0 ? void 0 : _this$options$filter.call(_this$options, badge, this.course);
      }) // Filter Badges
      .sort(this.badgeCompareFuncByPriority); // Sort by priority
    };
    /**
     * Get the badges by creating react elements of their corresponding components for the course based on the options
     * provided.
     * @returns an array of badge react elements
     */
    this.getBadgeComponents = () => {
      return this.getBadgeFamilies().map(this.getBadgeComponent) // Get Badge Component
      .filter(b => !!b) // Filter null or undefined
      .map((b, i) => b ? /*#__PURE__*/React.createElement(b, {
        key: i
      }) : null) // Render Badge Components
      .slice(0, this.options.numberOfBadgesToShow); // Get only the number of badges to show
    };
    this.course = course;
    this.options = {
      ...this.defaultOptions,
      ...options
    };
  }
}
//# sourceMappingURL=course-badges.react-component.js.map